import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  DollarSign, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Calendar,
  Users,
  FileText,
  ChevronRight,
  Sparkles,
  Target,
  Zap,
  BarChart3,
  Phone,
  MessageCircle
} from 'lucide-react';

// Types
interface ProspectData {
  companyName: string;
  ownerName: string;
  industry: 'residential' | 'commercial' | 'mixed';
  teamSize: number;
  monthlyQuotes: number;
  monthlyProjects: number;
  currentTools: string[];
}

// Parse URL parameters for personalization
function usePersonalization() {
  const [data, setData] = useState<ProspectData>({
    companyName: 'Your Design Studio',
    ownerName: 'there',
    industry: 'residential',
    teamSize: 5,
    monthlyQuotes: 12,
    monthlyProjects: 4,
    currentTools: ['Excel', 'WhatsApp', 'Email']
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    setData({
      companyName: params.get('company') || 'Your Design Studio',
      ownerName: params.get('name') || 'there',
      industry: (params.get('industry') as ProspectData['industry']) || 'residential',
      teamSize: parseInt(params.get('team') || '5'),
      monthlyQuotes: parseInt(params.get('quotes') || '12'),
      monthlyProjects: parseInt(params.get('projects') || '4'),
      currentTools: params.get('tools')?.split(',') || ['Excel', 'WhatsApp', 'Email']
    });
    
    // Simulate loading for dramatic effect
    setTimeout(() => setLoading(false), 1500);
  }, []);

  return { data, loading };
}

// ROI Calculator - Enhanced with Revenue Protection + Opportunity Cost
function calculateROI(data: ProspectData) {
  const timePerQuote = 60; // minutes
  const timePerQuoteAfter = 15; // minutes
  const timePerInvoice = 20; // minutes
  const timePerInvoiceAfter = 2; // minutes
  const hourlyRate = 50; // SGD
  const avgQuoteValue = 25000; // Average quotation value in SGD
  
  // 1. TIME SAVINGS
  const monthlyQuoteHours = (data.monthlyQuotes * timePerQuote) / 60;
  const monthlyQuoteHoursAfter = (data.monthlyQuotes * timePerQuoteAfter) / 60;
  const monthlyInvoiceHours = (data.monthlyProjects * 2 * timePerInvoice) / 60;
  const monthlyInvoiceHoursAfter = (data.monthlyProjects * 2 * timePerInvoiceAfter) / 60;
  
  const hoursSaved = (monthlyQuoteHours - monthlyQuoteHoursAfter) + 
                     (monthlyInvoiceHours - monthlyInvoiceHoursAfter);
  const timeSavings = hoursSaved * hourlyRate;
  
  // 2. REVENUE PROTECTION
  // Lost quotations (75% follow-up rate issue)
  const lostQuotesRate = 0.25; // 25% of quotes lost due to poor follow-up
  const lostRevenue = data.monthlyQuotes * avgQuoteValue * lostQuotesRate * 0.3; // 30% close rate on recovered leads
  
  // Unbilled variations (15-20% forgotten)
  const unbilledVariations = data.monthlyProjects * 1200 * 0.17; // Average SGD 1200 VO, 17% typically forgotten
  
  const revenueProtected = lostRevenue + unbilledVariations;
  
  // 3. OPPORTUNITY COST
  // Time saved could be spent on billable work
  const opportunityCost = hoursSaved * 80; // Assume SGD 80/hour billable rate
  
  // TOTAL VALUE
  const monthlyTotal = timeSavings + revenueProtected + opportunityCost;
  const yearlyTotal = monthlyTotal * 12;
  
  return {
    hoursSaved: Math.round(hoursSaved),
    timeSavings: Math.round(timeSavings),
    revenueProtected: Math.round(revenueProtected),
    opportunityCost: Math.round(opportunityCost),
    monthlyTotal: Math.round(monthlyTotal),
    yearlyTotal: Math.round(yearlyTotal),
    quotesFaster: Math.round(((timePerQuote - timePerQuoteAfter) / timePerQuote) * 100),
    invoicesFaster: Math.round(((timePerInvoice - timePerInvoiceAfter) / timePerInvoice) * 100),
    lostRevenue: Math.round(lostRevenue),
    unbilledVariations: Math.round(unbilledVariations)
  };
}

// Pain Points Generator
function getPainPoints(data: ProspectData) {
  const common = [
    {
      icon: Clock,
      image: '/images/03_card_quotes_stuck.png',
      title: `${Math.round(data.monthlyQuotes * 0.75)} quotes stuck in limbo`,
      description: `Based on industry data, ~75% of quotations sent by studios like yours never get a response. You're likely following up manually via WhatsApp and losing track.`
    },
    {
      icon: DollarSign,
      image: '/images/04_card_unbilled_variations.png',
      title: `SGD ${Math.round(data.monthlyProjects * 1500)} in unbilled variations`,
      description: `Most studios forget to bill 15-20% of variation orders. With ${data.monthlyProjects} projects/month, that's significant revenue leakage.`
    },
    {
      icon: AlertTriangle,
      image: '/images/05_card_commission_disputes.png',
      title: `${data.teamSize > 3 ? 'Commission disputes slowing you down' : 'No visibility on true profitability'}`,
      description: data.teamSize > 3
        ? `Teams your size spend 3-5 hours monthly resolving commission calculations. Staff disputes hurt morale.`
        : `Without proper tracking, you can't see which projects actually made money vs. which lost money.`
    }
  ];

  return common;
}

// Loading Screen
function LoadingScreen({ companyName }: { companyName: string }) {
  return (
    <div className="fixed inset-0 bg-primary-900 flex items-center justify-center z-50">
      <div className="text-center px-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 md:w-16 md:h-16 border-4 border-white border-t-transparent rounded-full mx-auto mb-6"
        />
        <h2 className="text-white text-lg md:text-xl font-semibold mb-2">Analyzing {companyName}...</h2>
        <p className="text-primary-200 text-sm">Generating your personalized report</p>
      </div>
    </div>
  );
}

// Sticky Header
function StickyHeader() {
  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-40">
      <div className="max-w-5xl mx-auto px-3 md:px-4 py-2 md:py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sage-600 font-heading font-bold text-sm md:text-lg">DesignSuite Labs</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="tel:+6591872356"
              className="flex items-center gap-1 md:gap-2 bg-sage-500 text-white px-3 md:px-4 py-2 rounded-full font-body font-medium text-xs md:text-sm hover:bg-sage-600 transition whitespace-nowrap"
            >
              <Phone className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden sm:inline">+65 9187 2356</span>
              <span className="sm:hidden">Call</span>
            </a>
            <a
              href="https://wa.me/6591872356"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 md:gap-2 bg-green-500 text-white px-3 md:px-4 py-2 rounded-full font-body font-medium text-xs md:text-sm hover:bg-green-600 transition whitespace-nowrap"
            >
              <MessageCircle className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
              <span className="sm:hidden">Chat</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

// Hero Section
function HeroSection({ data, roi }: { data: ProspectData; roi: ReturnType<typeof calculateROI> }) {
  return (
    <section className="relative bg-gradient-to-br from-sage-600 via-sage-500 to-sage-700 text-white py-16 md:py-28 px-4 overflow-hidden">
      {/* Background pattern - Scandinavian minimalism */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-sage-300 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center md:text-left"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6 md:mb-8 font-body"
            >
              <Sparkles className="w-4 h-4 text-sage-200" />
              <span className="text-sage-50 font-body">Personalized Report for {data.companyName}</span>
            </motion.span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading mb-5 md:mb-8 leading-tight">
              <span className="block text-white/90">Hi {data.ownerName},</span>
              <span className="block mt-2 bg-gradient-to-r from-beige-200 via-white to-beige-200 bg-clip-text text-transparent">
                You're losing SGD {roi.yearlyTotal.toLocaleString()}/year
              </span>
            </h1>

            <p className="text-base md:text-xl text-sage-50 max-w-2xl mb-8 md:mb-10 leading-relaxed font-body">
              We've analyzed <span className="text-white font-semibold">{data.industry}</span> studios like yours in Singapore.
              Here's exactly where your quotation process is bleeding time and money—and how to fix it.
            </p>

            <motion.a
              href="#roi"
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 bg-white text-sage-700 px-8 py-4 rounded-full font-heading text-base md:text-lg hover:bg-beige-100 transition-all shadow-lg"
            >
              See Your Numbers <ArrowRight className="w-5 h-5" />
            </motion.a>

            <p className="mt-4 text-sm text-sage-200 font-body">Takes 30 seconds • No obligation</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden md:block"
          >
            <img
              src="/images/01_hero_illustration.png"
              alt="Design studio owner overwhelmed by paperwork"
              className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// The Hook Section
function HookSection({ data }: { data: ProspectData }) {
  return (
    <section className="py-12 md:py-20 px-4 bg-white relative">
      {/* Decorative element */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sage-400 via-sage-500 to-sage-400"></div>
      
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-beige-200 border-l-4 border-sage-400 rounded-r-2xl shadow-lg overflow-hidden"
        >
          <img
            src="/images/02_market_change_card.png"
            alt="The shift from Excel spreadsheets to modern quotation systems"
            className="w-full object-contain bg-beige-100"
          />
          <div className="p-5 md:p-8">
            <div className="flex items-start gap-4">
              <div className="bg-sage-100 p-3 rounded-xl">
                <Target className="w-6 h-6 md:w-8 md:h-8 text-sage-600" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-heading mb-2 text-charcoal-500">
                  The Singapore Interior Design Market Has Changed
                </h3>
                <p className="text-charcoal-400 leading-relaxed font-body">
                  Studios that still rely on <span className="font-semibold text-sage-600">Excel + WhatsApp</span> are losing deals to competitors who respond faster and look more professional. Your clients expect instant quotations and digital contracts—but your current process takes {data.teamSize > 5 ? 'days' : 'hours'}.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Pain Points Section
function PainPointsSection({ data }: { data: ProspectData }) {
  const painPoints = getPainPoints(data);
  
  return (
    <section className="py-16 md:py-24 px-4 bg-beige-100">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-sage-100 text-sage-700 rounded-full text-sm font-heading font-semibold mb-4">
            THE PROBLEM
          </span>
          <h2 className="text-2xl md:text-4xl font-heading text-charcoal-500 mb-4">
            3 Problems Costing {data.companyName} Money Right Now
          </h2>
          <p className="text-charcoal-400 max-w-2xl mx-auto font-body">
            Based on <span className="font-semibold text-charcoal-500">{data.industry}</span> studios with <span className="font-semibold">{data.teamSize}</span> team members doing ~<span className="font-semibold">{data.monthlyQuotes}</span> quotes/month
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden group"
            >
              <img
                src={point.image}
                alt={point.title}
                className="w-full object-contain bg-beige-50"
              />
              <div className="p-6">
                <h3 className="text-lg md:text-xl font-heading mb-3 text-charcoal-500">{point.title}</h3>
                <p className="text-charcoal-400 leading-relaxed font-body">{point.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ROI Section
function ROISection({ data, roi }: { data: ProspectData; roi: ReturnType<typeof calculateROI> }) {
  return (
    <section id="roi" className="py-16 md:py-24 px-4 bg-gradient-to-br from-sage-600 via-sage-500 to-sage-700 text-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-sage-300/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-white/20 text-sage-100 rounded-full text-sm font-heading font-semibold mb-4">
            THE SOLUTION
          </span>
          <h2 className="text-2xl md:text-4xl font-heading mb-4">
            Your Personalized ROI Analysis
          </h2>
          <p className="text-sage-100 max-w-2xl mx-auto font-body">
            Here's exactly what DesignSuite Labs would save {data.companyName} every month
          </p>
        </motion.div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-5 md:p-6 rounded-2xl text-center"
          >
            <div className="bg-white/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Clock className="w-6 h-6 text-sage-300" />
            </div>
            <div className="text-3xl md:text-4xl font-heading mb-1">{roi.hoursSaved}</div>
            <div className="text-sage-200 text-sm font-body">Hours saved/month</div>
          </motion.div>
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-5 md:p-6 rounded-2xl text-center"
          >
            <div className="bg-white/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
              <DollarSign className="w-6 h-6 text-sage-300" />
            </div>
            <div className="text-3xl md:text-4xl font-heading mb-1">SGD {roi.timeSavings.toLocaleString()}</div>
            <div className="text-sage-200 text-sm font-body">Time savings/month</div>
          </motion.div>
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-5 md:p-6 rounded-2xl text-center"
          >
            <div className="bg-white/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-red-300" />
            </div>
            <div className="text-3xl md:text-4xl font-heading mb-1">SGD {roi.revenueProtected.toLocaleString()}</div>
            <div className="text-sage-200 text-sm font-body">Revenue protected/month</div>
          </motion.div>
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white p-5 md:p-6 rounded-2xl text-center col-span-2 lg:col-span-1"
          >
            <div className="bg-sage-100 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-sage-600" />
            </div>
            <div className="text-3xl md:text-4xl font-heading mb-1 text-sage-700">SGD {roi.yearlyTotal.toLocaleString()}</div>
            <div className="text-sage-600 text-sm font-body font-semibold">Total yearly value</div>
          </motion.div>
        </div>
        
        {/* Breakdown Section */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 mb-8">
          <h3 className="text-xl font-heading mb-6 text-center">
            Where Your SGD {roi.monthlyTotal.toLocaleString()}/month Comes From
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-sage-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-8 h-8 text-sage-300" />
              </div>
              <h4 className="font-heading font-bold text-lg mb-2">Time Savings</h4>
              <p className="text-sage-300 text-2xl font-heading font-bold">SGD {roi.timeSavings.toLocaleString()}/mo</p>
              <p className="text-sage-200 text-sm mt-1 font-body">{roi.hoursSaved} hours × SGD 50/hr</p>
            </div>
            <div className="text-center">
              <div className="bg-red-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-8 h-8 text-red-300" />
              </div>
              <h4 className="font-heading font-bold text-lg mb-2">Revenue Protection</h4>
              <p className="text-red-300 text-2xl font-heading font-bold">SGD {roi.revenueProtected.toLocaleString()}/mo</p>
              <p className="text-sage-200 text-sm mt-1 font-body">Lost quotes + unbilled VOs</p>
            </div>
            <div className="text-center">
              <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-8 h-8 text-green-300" />
              </div>
              <h4 className="font-heading font-bold text-lg mb-2">Opportunity Cost</h4>
              <p className="text-green-300 text-2xl font-heading font-bold">SGD {roi.opportunityCost.toLocaleString()}/mo</p>
              <p className="text-sage-200 text-sm mt-1 font-body">Billable hours recovered</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white text-gray-900 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary-600" />
            The Math Behind Your Numbers
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold mb-2 text-red-600">Current Process (Monthly)</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex justify-between">
                  <span>Quote creation time:</span>
                  <span className="font-medium">{data.monthlyQuotes * 60} mins</span>
                </li>
                <li className="flex justify-between">
                  <span>Invoice generation:</span>
                  <span className="font-medium">{data.monthlyProjects * 2 * 20} mins</span>
                </li>
                <li className="flex justify-between">
                  <span>Follow-up tracking:</span>
                  <span className="font-medium">Manual/sporadic</span>
                </li>
                <li className="flex justify-between border-t pt-2">
                  <span className="font-semibold">Total time:</span>
                  <span className="font-semibold">{Math.round((data.monthlyQuotes * 60 + data.monthlyProjects * 40) / 60)} hours</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-green-600">With DesignSuite Labs</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex justify-between">
                  <span>Quote creation time:</span>
                  <span className="font-medium">{data.monthlyQuotes * 15} mins</span>
                </li>
                <li className="flex justify-between">
                  <span>Invoice generation:</span>
                  <span className="font-medium">{data.monthlyProjects * 2 * 2} mins</span>
                </li>
                <li className="flex justify-between">
                  <span>Follow-up tracking:</span>
                  <span className="font-medium">Automated pipeline</span>
                </li>
                <li className="flex justify-between border-t pt-2">
                  <span className="font-semibold">Total time:</span>
                  <span className="font-semibold">{Math.round((data.monthlyQuotes * 15 + data.monthlyProjects * 4) / 60)} hours</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Solution Section
function SolutionSection({ data }: { data: ProspectData }) {
  const features = [
    {
      icon: FileText,
      image: '/images/06_feature_quotation_builder.png',
      title: 'Drag-and-Drop Quotation Builder',
      benefit: 'Send professional quotes in 15 minutes instead of an hour',
      description: 'Your team drags items from your catalog, adjusts quantities, and generates a branded PDF instantly. No more copy-pasting from Excel.'
    },
    {
      icon: CheckCircle,
      image: '/images/07_feature_digital_signing.png',
      title: 'Digital Contract Signing',
      benefit: 'Close deals same-day instead of waiting a week',
      description: 'Send a secure signing link to your client. They sign on their phone. You get notified instantly. No more print-sign-scan delays.'
    },
    {
      icon: Users,
      image: '/images/08_feature_commission_tracking.png',
      title: 'Commission Tracking',
      benefit: 'Zero disputes, complete transparency',
      description: data.teamSize > 3
        ? 'Automatic profit-based commission calculations. Every staff member sees exactly how their payout was calculated.'
        : 'See true project profitability in real-time. Know which projects made money and which didn\'t.'
    },
    {
      icon: TrendingUp,
      image: '/images/09_feature_ar_dashboard.png',
      title: 'AR Aging Dashboard',
      benefit: 'Get paid faster with automated reminders',
      description: 'See at a glance who owes you money and for how long. Send automated payment reminders without awkward conversations.'
    }
  ];

  return (
    <section className="py-16 md:py-24 px-4 bg-white relative">
      {/* Decorative top line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sage-400 via-sage-500 to-sage-400"></div>
      
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-sage-100 text-sage-700 rounded-full text-sm font-heading font-semibold mb-4">
            THE SOLUTION
          </span>
          <h2 className="text-2xl md:text-4xl font-heading text-charcoal-500 mb-4">
            Here's How {data.companyName} Fixes These Problems
          </h2>
          <p className="text-charcoal-400 max-w-2xl mx-auto font-body">
            DesignSuite Labs replaces your Excel + WhatsApp chaos with one integrated system
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="bg-beige-100 rounded-2xl shadow-lg border border-beige-200 overflow-hidden"
            >
              <img
                src={feature.image}
                alt={feature.title}
                className="w-full object-contain bg-white"
              />
              <div className="p-6 md:p-8">
                <h3 className="text-lg md:text-xl font-heading mb-2 text-charcoal-500">{feature.title}</h3>
                <p className="text-sage-600 font-semibold mb-3 text-sm md:text-base font-body">{feature.benefit}</p>
                <p className="text-charcoal-400 leading-relaxed font-body">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Social Proof Section
function SocialProofSection({ data }: { data: ProspectData }) {
  const testimonials = [
    {
      quote: "We were spending 2-3 hours on every quotation. Now it's 20 minutes. That's 40+ hours saved every month.",
      author: "Design Studio Owner",
      company: "Singapore Renovation Company",
      metric: "40+ hours saved/month",
      avatar: "/images/11_testimonial_chinese_owner.png"
    },
    {
      quote: "The digital signing feature alone paid for the system. We close deals in 24 hours instead of a week.",
      author: "Sales Manager",
      company: "Interior Design Firm",
      metric: "5x faster deal closing",
      avatar: "/images/12_testimonial_malay_manager.png"
    },
    {
      quote: "No more commission arguments. Everyone sees exactly how payouts are calculated. Team morale improved instantly.",
      author: "Operations Director",
      company: "Multi-branch Studio",
      metric: "Zero commission disputes",
      avatar: "/images/13_testimonial_indian_director.png"
    }
  ];

  return (
    <section className="py-16 md:py-24 px-4 bg-beige-100">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-sage-100 text-sage-700 rounded-full text-sm font-heading font-semibold mb-4">
            SOCIAL PROOF
          </span>
          <h2 className="text-2xl md:text-4xl font-heading text-charcoal-500 mb-4">
            Singapore Studios Using DesignSuite Labs
          </h2>
          <p className="text-charcoal-400 max-w-2xl mx-auto font-body">
            Join {data.teamSize}+ other studios transforming their business
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8 relative"
            >
              <div className="absolute -top-3 left-6 text-5xl text-sage-200 font-serif font-heading">"</div>
              <p className="text-charcoal-400 mb-6 mt-4 relative z-10 leading-relaxed font-body">{testimonial.quote}</p>
              <div className="border-t pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover border-2 border-sage-200"
                  />
                  <div>
                    <div className="font-heading font-semibold text-charcoal-500">{testimonial.author}</div>
                    <div className="text-sm text-charcoal-300 font-body">{testimonial.company}</div>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 bg-sage-100 text-sage-700 px-4 py-1.5 rounded-full text-sm font-heading font-semibold">
                  <CheckCircle className="w-4 h-4" />
                  {testimonial.metric}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection({ data }: { data: ProspectData }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would send to your backend/CRM
    setSubmitted(true);
  };

  return (
    <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-sage-600 via-sage-500 to-sage-700 text-white relative overflow-hidden">
      {/* Background illustration */}
      <img
        src="/images/10_cta_banner_illustration.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
      />
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-sage-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-block px-4 py-1.5 bg-white/20 text-sage-100 rounded-full text-sm font-heading font-semibold mb-6">
            READY TO TRANSFORM?
          </span>
          <h2 className="text-2xl md:text-4xl font-heading mb-5">
            Ready to stop losing <span className="text-beige-200">SGD {calculateROI(data).yearlyTotal.toLocaleString()}</span> every year?
          </h2>
          <p className="text-base md:text-xl text-sage-50 mb-8 md:mb-10 font-body">
            Book a 20-minute demo. We'll show you exactly how DesignSuite Labs works for a {data.industry} studio like {data.companyName}.
          </p>
        </motion.div>
        
        <AnimatePresence mode="wait">
          {!showForm && !submitted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.button
                onClick={() => setShowForm(true)}
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-sage-700 px-8 py-4 rounded-full font-heading text-base md:text-xl hover:bg-beige-100 transition-all shadow-lg inline-flex items-center gap-3"
              >
                <Calendar className="w-5 h-5 md:w-6 md:h-6" />
                Book Your Free Demo
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </motion.button>
              <p className="mt-5 text-sage-200 text-sm font-body">
                ✓ No commitment &nbsp; ✓ No credit card &nbsp; ✓ 14-day free trial
              </p>
            </motion.div>
          )}
          
          {showForm && !submitted && (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onSubmit={handleSubmit}
              className="bg-beige-100 text-charcoal-500 p-8 rounded-xl max-w-md mx-auto"
            >
              <h3 className="text-xl font-heading font-bold mb-4">Book Your Demo</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Your name"
                  required
                  className="w-full px-4 py-3 border border-beige-200 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none bg-white font-body"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <input
                  type="email"
                  placeholder="Email address"
                  required
                  className="w-full px-4 py-3 border border-beige-200 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none bg-white font-body"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <input
                  type="tel"
                  placeholder="Phone number"
                  required
                  className="w-full px-4 py-3 border border-beige-200 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none bg-white font-body"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
                <button
                  type="submit"
                  className="w-full bg-sage-500 text-white py-3 rounded-lg font-heading font-semibold hover:bg-sage-600 transition"
                >
                  Confirm Demo Booking
                </button>
              </div>
            </motion.form>
          )}
          
          {submitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-500 text-white p-8 rounded-xl"
            >
              <CheckCircle className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Demo Request Received!</h3>
              <p className="text-lg">
                We'll contact you within 24 hours to schedule your personalized demo for {data.companyName}.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// FAQ Section
function FAQSection() {
  const faqs = [
    {
      q: "How long does implementation take?",
      a: "Most studios are fully operational within 3-5 days. We help import your item catalog and train your team in one session."
    },
    {
      q: "Can we import our existing quotations?",
      a: "Yes. You can either start fresh with active projects or we can help migrate recent quotations from Excel."
    },
    {
      q: "What if we have multiple companies/branches?",
      a: "DesignSuite Labs supports multi-company setups. Each branch can have separate branding, bank accounts, and staff permissions."
    },
    {
      q: "Is our data secure?",
      a: "Your data is stored on Supabase infrastructure with bank-grade encryption. We use the same security standards as major banks."
    },
    {
      q: "What's the contract term?",
      a: "Month-to-month. No long-term lock-in. If it doesn't work for you, cancel anytime with 30 days notice."
    }
  ];

  return (
    <section className="py-20 px-4 bg-beige-100">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-heading text-center mb-12 text-charcoal-500">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
              className="bg-white border border-beige-200 rounded-lg p-6 hover:shadow-md transition"
            >
              <h3 className="font-heading font-bold text-lg mb-2 text-charcoal-500">{faq.q}</h3>
              <p className="text-charcoal-400 font-body">{faq.a}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer className="bg-charcoal-500 text-white py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <h3 className="text-white font-heading font-bold text-lg">DesignSuite Labs</h3>
            <p className="text-charcoal-300 font-body text-sm">ERP software for Singapore interior design studios</p>
          </div>
          <div className="flex gap-6 font-body text-sm">
            <a href="#" className="text-charcoal-300 hover:text-white transition">Privacy</a>
            <a href="#" className="text-charcoal-300 hover:text-white transition">Terms</a>
            <a href="#" className="text-charcoal-300 hover:text-white transition">Contact</a>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-charcoal-400 text-center text-sm text-charcoal-300 font-body">
          © 2025 DesignSuite Labs. Built for Singapore interior design businesses.
        </div>
      </div>
    </footer>
  );
}

// Main App
function App() {
  const { data, loading } = usePersonalization();
  const roi = calculateROI(data);

  if (loading) {
    return <LoadingScreen companyName={data.companyName} />;
  }

  return (
    <div className="min-h-screen">
      <StickyHeader />
      <div className="pt-12 md:pt-14">
        <HeroSection data={data} roi={roi} />
        <HookSection data={data} />
        <PainPointsSection data={data} />
        <ROISection data={data} roi={roi} />
        <SolutionSection data={data} />
        <SocialProofSection data={data} />
        <CTASection data={data} />
        <FAQSection />
      </div>
      <Footer />
    </div>
  );
}

export default App;